import { useState, useMemo, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Card, Button, PageLoader, AddChildModal } from "../components";
import {
  ChildDashboardCard,
  StarCounter,
  ActivityTimeline,
  type ActivityItem,
} from "../components/dashboard";
import { ReferralWidget, ShareModal } from "../components/referrals";
import { useParentDashboard, usePageTitle, useReferrals } from "../hooks";
import { useAuth } from "../contexts";
import { apiGet } from "../lib/api";
import type { Subscription } from "@jumpbean/shared";

/**
 * ParentDashboardPage - Parent dashboard for tracking children's progress.
 *
 * Features:
 * - Overview of all children's progress
 * - Aggregate family stats
 * - Recent activity feed
 * - Quick links to manage account
 * - Add new child profiles
 */
export function ParentDashboardPage() {
  usePageTitle("Parent Dashboard");

  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const { data, isLoading, error, refetch } = useParentDashboard();
  const {
    code: referralCode,
    stats: referralStats,
    isLoading: referralLoading,
    isGenerating: referralGenerating,
    generateCode: generateReferralCode,
  } = useReferrals();
  const [expandedChildId, setExpandedChildId] = useState<string | null>(null);
  const [showAddChildModal, setShowAddChildModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [subscription, setSubscription] = useState<Subscription | null>(null);

  // Fetch subscription to determine referral eligibility
  useEffect(() => {
    if (user) {
      apiGet<{ subscription: Subscription | null }>("/api/billing/subscription")
        .then(({ data }) => {
          if (data?.subscription) {
            setSubscription(data.subscription);
          }
        })
        .catch(() => {
          // Subscription fetch failed, user may not have one (free tier)
        });
    }
  }, [user]);

  // Determine referral eligibility - Family or Classroom tier with active status
  const isReferralEligible = useMemo(() => {
    if (!subscription) return false;
    const isPaidTier = subscription.tier === "family" || subscription.tier === "classroom";
    const isActive = subscription.status === "active";
    return isPaidTier && isActive;
  }, [subscription]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login", { replace: true });
    }
  }, [user, authLoading, navigate]);

  // Build activity feed from children's recent badges
  const recentActivities = useMemo<ActivityItem[]>(() => {
    if (!data) return [];

    const activities: ActivityItem[] = [];

    // Collect recent badges from all children
    data.children.forEach((child) => {
      child.recentBadges.forEach((badge) => {
        if (badge.badge && badge.discipline) {
          activities.push({
            id: `${child.id}-${badge.id}`,
            type: "badge",
            title: `${child.displayName} earned "${badge.badge.name}"`,
            description: `${badge.discipline.name} Level ${badge.level?.number ?? 1}`,
            timestamp: badge.earnedAt,
            discipline: badge.discipline.slug,
            emoji: "🏅",
          });
        }
      });

      // Add streak achievements
      if (child.stats.currentStreakDays >= 3) {
        activities.push({
          id: `${child.id}-streak`,
          type: "streak",
          title: `${child.displayName} is on a ${child.stats.currentStreakDays}-day streak!`,
          description: "Keep up the great work!",
          timestamp: child.stats.lastPlayedAt ?? new Date().toISOString(),
          emoji: "🔥",
        });
      }
    });

    // Sort by timestamp descending
    return activities.sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  }, [data]);

  // Format total play time
  function formatTotalPlayTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hours === 0) return `${mins} minutes`;
    if (mins === 0) return `${hours} hours`;
    return `${hours}h ${mins}m`;
  }

  // Handle adding new child success
  const handleAddChildSuccess = () => {
    setShowAddChildModal(false);
    refetch();
  };

  // Loading state
  if (authLoading || isLoading) {
    return <PageLoader message="Loading your dashboard..." />;
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12">
        <div className="text-center max-w-md">
          <span className="text-6xl mb-4 block">😢</span>
          <h1 className="font-display font-bold text-display-md text-neutral-800 mb-2">
            Oops! Something went wrong
          </h1>
          <p className="font-body text-body-lg text-neutral-600 mb-6">{error}</p>
          <Button onClick={() => refetch()}>Try Again</Button>
        </div>
      </div>
    );
  }

  // Empty state - no children yet
  if (!data || data.children.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-neutral-50 to-magic-50/30">
        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="text-center mb-12">
            <h1 className="font-display font-extrabold text-display-lg text-magic-600 mb-2">
              Welcome to JumpBean!
            </h1>
            <p className="font-body text-body-lg text-neutral-600">
              Let's get started by adding your first child profile.
            </p>
          </div>

          <Card variant="elevated" className="p-8 text-center">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-sunshine-200 to-fairy-200 flex items-center justify-center">
              <span className="text-5xl">👶</span>
            </div>
            <h2 className="font-display font-bold text-display-sm text-neutral-800 mb-3">
              Add Your First Child
            </h2>
            <p className="font-body text-body-md text-neutral-600 mb-6 max-w-sm mx-auto">
              Create a profile for your child to start their magical learning adventure!
            </p>
            <Button size="lg" onClick={() => setShowAddChildModal(true)}>
              Add Child Profile
            </Button>
          </Card>

          {/* Quick links */}
          <div className="mt-8 flex justify-center gap-6">
            <Link
              to="/settings"
              className="font-body text-sm text-magic-500 hover:text-magic-600 transition-colors"
            >
              Settings
            </Link>
            <Link
              to="/pricing"
              className="font-body text-sm text-magic-500 hover:text-magic-600 transition-colors"
            >
              View Plans
            </Link>
          </div>
        </div>

        {/* Add Child Modal */}
        <AddChildModal
          isOpen={showAddChildModal}
          onClose={() => setShowAddChildModal(false)}
          onSuccess={handleAddChildSuccess}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-neutral-50 via-cloud to-magic-50/20">
      {/* Decorative background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 -left-20 w-64 h-64 bg-sunshine-200/20 rounded-full blur-3xl" />
        <div className="absolute top-40 -right-20 w-80 h-80 bg-magic-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-1/3 w-72 h-72 bg-meadow-200/15 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-4xl mx-auto px-4 py-8">
        {/* Header Section */}
        <header className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="font-display font-extrabold text-display-md text-neutral-800">
                Parent Dashboard
              </h1>
              <p className="font-body text-body-lg text-neutral-500 mt-1">
                Track your family's learning journey
              </p>
            </div>
            <Link
              to="/settings"
              className="p-3 rounded-xl bg-cloud hover:bg-neutral-100 transition-colors shadow-sm"
              aria-label="Settings"
            >
              <svg className="w-6 h-6 text-neutral-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </Link>
          </div>

          {/* Family Stats Summary */}
          <Card variant="elevated" className="p-5">
            <div className="flex items-center justify-between flex-wrap gap-4">
              {/* Total Stars */}
              <StarCounter
                count={data.aggregateTotals.totalStars}
                size="md"
                label="Family Stars"
              />

              {/* Divider */}
              <div className="hidden sm:block w-px h-12 bg-neutral-200" />

              {/* Total Badges */}
              <div className="flex items-center gap-2">
                <span className="text-3xl">🏅</span>
                <div>
                  <p className="font-display font-bold text-2xl text-magic-600">
                    {data.aggregateTotals.totalBadges}
                  </p>
                  <p className="font-body text-sm text-neutral-500">Badges Earned</p>
                </div>
              </div>

              {/* Divider */}
              <div className="hidden sm:block w-px h-12 bg-neutral-200" />

              {/* Total Play Time */}
              <div className="flex items-center gap-2">
                <span className="text-3xl">⏱️</span>
                <div>
                  <p className="font-display font-bold text-2xl text-meadow-600">
                    {formatTotalPlayTime(data.aggregateTotals.totalTimePlayedSeconds)}
                  </p>
                  <p className="font-body text-sm text-neutral-500">Total Play Time</p>
                </div>
              </div>
            </div>
          </Card>
        </header>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Children Cards - Left Column (2 cols on lg) */}
          <div className="lg:col-span-2 space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-bold text-display-sm text-neutral-800">
                Your Children
              </h2>
              {data.canAddMore && (
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() => setShowAddChildModal(true)}
                >
                  <span className="mr-1">+</span> Add Child
                </Button>
              )}
            </div>

            {data.children.map((child) => (
              <ChildDashboardCard
                key={child.id}
                child={child}
                isExpanded={expandedChildId === child.id}
                onToggleExpand={() =>
                  setExpandedChildId(expandedChildId === child.id ? null : child.id)
                }
                onSettingsSaved={refetch}
              />
            ))}

            {/* Child limit message */}
            {!data.canAddMore && (
              <Card variant="default" className="p-4 border-dashed border-2 border-neutral-200">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">👨‍👩‍👧‍👦</span>
                  <div className="flex-1">
                    <p className="font-body text-sm text-neutral-600">
                      You've reached your limit of {data.childLimit} child
                      {data.childLimit > 1 ? " profiles" : " profile"}.
                    </p>
                    <Link
                      to="/pricing"
                      className="font-body text-sm font-semibold text-magic-500 hover:text-magic-600"
                    >
                      Upgrade for more →
                    </Link>
                  </div>
                </div>
              </Card>
            )}
          </div>

          {/* Right Sidebar */}
          <div className="space-y-5">
            {/* Recent Activity */}
            <Card variant="elevated" className="p-5">
              <h3 className="font-display font-bold text-body-lg text-neutral-800 mb-4">
                Recent Activity
              </h3>
              <ActivityTimeline
                activities={recentActivities}
                maxItems={5}
                emptyMessage="Activity will appear as your children play!"
              />
            </Card>

            {/* Referral Widget */}
            {!referralLoading && (
              <ReferralWidget
                code={referralCode}
                stats={referralStats}
                isEligible={isReferralEligible}
                isGenerating={referralGenerating}
                onGenerateCode={generateReferralCode}
                onShare={() => setShowShareModal(true)}
              />
            )}

            {/* Quick Actions */}
            <Card variant="default" className="p-5">
              <h3 className="font-display font-bold text-body-lg text-neutral-800 mb-4">
                Quick Actions
              </h3>
              <div className="space-y-2">
                <Link
                  to="/profiles"
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-neutral-50 transition-colors group"
                >
                  <span className="text-xl">🎮</span>
                  <span className="font-body font-medium text-neutral-700 group-hover:text-magic-600">
                    Switch to Play Mode
                  </span>
                </Link>
                <Link
                  to="/pricing"
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-neutral-50 transition-colors group"
                >
                  <span className="text-xl">💎</span>
                  <span className="font-body font-medium text-neutral-700 group-hover:text-magic-600">
                    Manage Subscription
                  </span>
                </Link>
                <Link
                  to="/settings"
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-neutral-50 transition-colors group"
                >
                  <span className="text-xl">⚙️</span>
                  <span className="font-body font-medium text-neutral-700 group-hover:text-magic-600">
                    Account Settings
                  </span>
                </Link>
              </div>
            </Card>

            {/* Tips Card */}
            <Card
              variant="default"
              className="p-5 bg-gradient-to-br from-sunshine-50 to-fairy-50 border-sunshine-200"
            >
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">💡</span>
                <div>
                  <h4 className="font-display font-semibold text-body-md text-neutral-800 mb-1">
                    Learning Tip
                  </h4>
                  <p className="font-body text-sm text-neutral-600">
                    Regular short sessions (5-10 min) are more effective than longer occasional ones.
                    Encourage daily play for the best learning outcomes!
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Add Child Modal */}
      <AddChildModal
        isOpen={showAddChildModal}
        onClose={() => setShowAddChildModal(false)}
        onSuccess={handleAddChildSuccess}
      />

      {/* Share Referral Modal */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        code={referralCode}
      />
    </div>
  );
}

export default ParentDashboardPage;
